import { Location } from '@angular/common';
import { Injectable, Injector, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { EnvironmentService } from 'mbs-ui-shared@services/environments.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { getSentryEnvironment } from '../utils/get-sentry-environment';

@Injectable()
export class AppInitService {
  private injector = inject(Injector);
  private location = inject(Location);
  private config = inject(ConfigurationService);
  // Lazy getters to break circular dependencies:
  // APP_INITIALIZER → AppInitService → AuthFacade/EnvironmentService → ... [CIRCULAR]
  // Both are only used inside init(), not in the constructor body.
  private get auth(): AuthFacade {
    return this.injector.get(AuthFacade);
  }

  private get environment(): EnvironmentService {
    return (this._environment ??= this.injector.get(EnvironmentService));
  }
  private _environment: EnvironmentService;

  private get isUnauthorizedRoute() {
    const unauthorizedRoute = '/AS/';
    return this.location.path().startsWith(unauthorizedRoute);
  }

  init(): Observable<boolean> {
    const initSentry$ = () => {
      const isSentryAvailableConfigValue = this.config.get('isSentryAvailable');
      const uiVersion = this.config.get('uiversion') || 'unspecified';

      const isSentryAvailable = !(isSentryAvailableConfigValue === false || isSentryAvailableConfigValue === 'false');

      if (!this.environment.production || !isSentryAvailable) return of(false);

      Sentry.init({
        dsn: this.environment.sentry.dsn,
        release: uiVersion,
        integrations: [
          Sentry.breadcrumbsIntegration({
            console: false,
            dom: true,
            fetch: false,
            history: false,
            xhr: false
          }),
          Sentry.browserTracingIntegration({
            instrumentNavigation: true,
            instrumentPageLoad: true
          })
        ],
        beforeBreadcrumb: (breadcrumb, hint?) => {
          if (breadcrumb.category === 'ui.click') {
            const innerText = hint?.event?.target?.innerText;

            if (!innerText || hint?.event?.target?.children?.length) return breadcrumb;

            breadcrumb.data = { target: innerText };
          }

          return breadcrumb;
        },
        tracesSampleRate: 1,
        environment: getSentryEnvironment()
      });
      Sentry.getGlobalScope().setTag('environment', getSentryEnvironment());

      if (!this.auth.authToken && !this.auth.isOneTimeSession && this.environment.useSessionToken && !this.isUnauthorizedRoute) {
        return this.auth.authTokenBySession$.pipe(
          catchError(() => of(false)),
          map(() => true)
        );
      }

      return of(true);
    };

    return initSentry$();
  }
}
